import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios, formatDateUS } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { BottomNav } from "@/components/shared/Navigation";
import {
  Heart, CheckCircle, Gift, X, ThumbsUp, ThumbsDown, SkipForward,
  Loader2, ChevronDown, Package, ClipboardList, AlertTriangle, Clock, ArrowLeft,
} from "lucide-react";
import { getCategoryColor } from "@/lib/categoryColors";
import { trackPageView, trackError, trackTabSwitch } from "@/lib/track";
import { toast } from "sonner";

const TABS = [
  { id: "saved", label: "Saved", icon: Heart, emptyIcon: Heart, emptyTitle: "No saved deals yet", emptyDesc: "Tap the heart on any deal to save it here" },
  { id: "planning", label: "Planning", icon: CheckCircle, emptyIcon: CheckCircle, emptyTitle: "Nothing in progress", emptyDesc: "Sign up for deals and they'll appear here" },
  { id: "claimed", label: "Claimed", icon: Gift, emptyIcon: Gift, emptyTitle: "No claims yet", emptyDesc: "Claim your birthday deals and track results here" },
];

export default function MyDealsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("saved");
  const [favorites, setFavorites] = useState([]);
  const [trackerItems, setTrackerItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);
  const [redemptionModal, setRedemptionModal] = useState(null);
  const [redemptionResult, setRedemptionResult] = useState("");
  const [redemptionNotes, setRedemptionNotes] = useState("");
  const [daysUntilBirthday, setDaysUntilBirthday] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [favRes, trackerRes, statsRes] = await Promise.all([
        axios.get(`${API}/favorites`),
        axios.get(`${API}/tracker`),
        axios.get(`${API}/stats`).catch(() => ({ data: {} })),
      ]);
      setFavorites(favRes.data);
      setTrackerItems(trackerRes.data);
      setDaysUntilBirthday(statsRes.data?.days_until_birthday ?? null);
    } catch (e) { trackError("my_deals_fetch", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { trackPageView("my_deals"); }, []);

  const removeFavorite = async (businessId) => {
    try { await axios.post(`${API}/favorites/${businessId}`); setFavorites(prev => prev.filter(f => f.business_id !== businessId)); }
    catch (e) { trackError("favorite_remove", e); }
  };

  const removeTracker = async (businessId) => {
    try { await axios.delete(`${API}/tracker/${businessId}`); setTrackerItems(prev => prev.filter(t => t.business_id !== businessId)); }
    catch (e) { trackError("tracker_remove", e); }
  };

  const handleRedemption = async (businessId) => {
    if (!redemptionResult) return;
    try {
      await axios.post(`${API}/tracker/${businessId}/redemption`, { result: redemptionResult, notes: redemptionNotes });
      setRedemptionModal(null); setRedemptionResult(""); setRedemptionNotes("");
      fetchData();
    } catch (e) { trackError("redemption", e); }
  };

  const planning = useMemo(() => trackerItems.filter(t => t.signed_up && !t.claimed), [trackerItems]);
  const claimed = useMemo(() => trackerItems.filter(t => t.claimed), [trackerItems]);

  // Urgency classification for planning items
  const { needsAttention, youreGood } = useMemo(() => {
    const needs = [];
    const good = [];
    for (const item of planning) {
      // Items "need attention" if they're not in the itinerary, or birthday is within 7 days
      const needsAction = !item.in_itinerary || (daysUntilBirthday != null && daysUntilBirthday <= 7);
      if (needsAction) {
        needs.push(item);
      } else {
        good.push(item);
      }
    }
    return { needsAttention: needs, youreGood: good };
  }, [planning, daysUntilBirthday]);

  const groupedFavorites = useMemo(() => {
    const map = {};
    favorites.forEach(fav => {
      const cat = fav.category || "Other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(fav);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [favorites]);

  const toggleSection = (key) => setOpenSection(openSection === key ? null : key);

  const counts = { saved: favorites.length, planning: planning.length, claimed: claimed.length };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="my-deals-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 p-4 pt-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 transition" data-testid="my-deals-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-sky-500" />My Deals
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{favorites.length + trackerItems.length} total deals</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 pt-3 pb-0" data-testid="my-deals-tabs">
        <div className="flex gap-1">
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setOpenSection(null); trackTabSwitch("my_deals", tab.id); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold rounded-t-xl transition-all ${isActive ? "bg-slate-50 text-sky-600 border-b-2 border-sky-500" : "text-slate-400 hover:text-slate-600"}`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive && tab.id === "saved" ? "fill-current" : ""}`} />
                {tab.label}
                {counts[tab.id] > 0 && (
                  <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? "bg-sky-500 text-white" : "bg-slate-200 text-slate-500"}`}>{counts[tab.id]}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 mt-4 flex flex-col gap-2">
        {/* SAVED TAB */}
        {activeTab === "saved" && (
          <>
            <div className="bg-pink-50 border border-pink-100 rounded-xl px-3.5 py-2.5 mb-1" data-testid="favorites-description">
              <p className="text-xs text-pink-700 font-medium">Your personal saved list — businesses you like and want to remember. Use these to build itineraries or track places to claim.</p>
            </div>
            {groupedFavorites.map(([category, items]) => {
              const cc = getCategoryColor(category);
              return (
                <div key={category} className="rounded-2xl overflow-hidden shadow-md" data-testid={`saved-section-${category.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                  <button onClick={() => toggleSection(category)} className={`w-full flex items-center gap-3 px-4 py-3.5 ${cc.gradient} text-white transition-all`}>
                    <Heart className="w-5 h-5" />
                    <span className="text-sm font-bold flex-1 text-left">{category}</span>
                    <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">{items.length}</span>
                    <motion.div animate={{ rotate: openSection === category ? 180 : 0 }} transition={{ duration: 0.2 }}><ChevronDown className="w-5 h-5" /></motion.div>
                  </button>
                  <AnimatePresence>
                    {openSection === category && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: "easeInOut" }} className="bg-white overflow-hidden">
                        <div className="p-3 max-h-[400px] overflow-y-auto space-y-2">
                          {items.map((fav) => (
                            <motion.div key={fav.business_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${cc.light} border ${cc.border} rounded-xl p-3 flex items-center gap-3`}>
                              <BrandLogo name={fav.business_name} logoUrl={fav.logo_url} size="sm" />
                              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/offer/${fav.business_id}`)}>
                                <h3 className="text-sm font-semibold text-slate-800 truncate">{fav.business_name}</h3>
                                <p className={`text-xs ${cc.text} font-medium`}>{fav.category}{fav.offer_value_est ? <span className="text-green-600"> · ${fav.offer_value_est}</span> : ""}</p>
                              </div>
                              <button onClick={() => removeFavorite(fav.business_id)} className="p-2 rounded-full" data-testid={`unsave-${fav.business_id}`}>
                                <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                              </button>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            {favorites.length === 0 && <EmptyState tab={TABS[0]} onAction={() => navigate("/")} actionLabel="Browse Deals" />}
          </>
        )}

        {/* PLANNING TAB */}
        {activeTab === "planning" && (
          <>
            {/* Smart context header */}
            {planning.length > 0 && daysUntilBirthday != null && (
              <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm" data-testid="planning-context-header">
                <p className="text-sm font-semibold text-slate-800">
                  {needsAttention.length === 0 && daysUntilBirthday <= 0
                    ? "Everything is ready — enjoy your birthday!"
                    : needsAttention.length === 0
                    ? "You're all set for now"
                    : daysUntilBirthday <= 7
                    ? `Your birthday is in ${Math.max(daysUntilBirthday, 0)} day${daysUntilBirthday !== 1 ? "s" : ""} — ${needsAttention.length} deal${needsAttention.length !== 1 ? "s" : ""} need${needsAttention.length === 1 ? "s" : ""} attention`
                    : `${needsAttention.length} deal${needsAttention.length !== 1 ? "s" : ""} need${needsAttention.length === 1 ? "s" : ""} your attention this week`}
                </p>
              </div>
            )}

            {/* Needs Attention section */}
            {needsAttention.length > 0 && (
              <div data-testid="needs-attention-section">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mt-2 mb-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />Needs Attention
                </p>
                <div className="space-y-2">
                  {needsAttention.map((item) => (
                    <PlanningCard key={item.business_id} item={item} navigate={navigate} setRedemptionModal={setRedemptionModal} removeTracker={removeTracker} fetchData={fetchData} daysUntilBirthday={daysUntilBirthday} />
                  ))}
                </div>
              </div>
            )}

            {/* You're good section */}
            {youreGood.length > 0 && (
              <div data-testid="youre-good-section">
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mt-3 mb-1.5 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />You're Good For Now
                </p>
                <div className="space-y-2">
                  {youreGood.map((item) => (
                    <PlanningCard key={item.business_id} item={item} navigate={navigate} setRedemptionModal={setRedemptionModal} removeTracker={removeTracker} fetchData={fetchData} daysUntilBirthday={daysUntilBirthday} />
                  ))}
                </div>
              </div>
            )}

            {/* Done for now celebration */}
            {needsAttention.length === 0 && planning.length > 0 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" data-testid="all-set-card">
                <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-700">You're all set for now</p>
                <p className="text-xs text-emerald-600 mt-1">All your deals are on track. Enjoy the countdown!</p>
              </motion.div>
            )}

            {planning.length === 0 && <EmptyState tab={TABS[1]} onAction={() => navigate("/")} actionLabel="Find Deals" />}
          </>
        )}

        {/* CLAIMED TAB */}
        {activeTab === "claimed" && (
          <>
            {claimed.length > 0 && (
              <div className="space-y-2">
                {claimed.map((item) => {
                  const cc = getCategoryColor(item.category);
                  return (
                    <div key={item.business_id} className={`${cc.light} border ${cc.border} rounded-xl p-3 flex items-center gap-3`}>
                      <BrandLogo name={item.business_name} logoUrl={item.logo_url} size="sm" />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/offer/${item.business_id}`)}>
                        <h3 className="text-sm font-semibold text-slate-800 truncate">{item.business_name}</h3>
                        <p className={`text-xs ${cc.text} font-medium`}>{item.category} {item.claimed_date ? `· ${formatDateUS(item.claimed_date)}` : ""}</p>
                      </div>
                      {item.redemption_result && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${item.redemption_result === "success" ? "bg-green-100 text-green-700" : item.redemption_result === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {item.redemption_result}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {claimed.length === 0 && <EmptyState tab={TABS[2]} onAction={() => navigate("/")} actionLabel="Start Claiming" />}
          </>
        )}
      </div>

      {/* Redemption Modal */}
      {redemptionModal && (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-end" onClick={() => setRedemptionModal(null)}>
          <div className="w-full bg-white rounded-t-3xl p-6 pb-20 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800">How did it go at {redemptionModal.business_name}?</h3>
            <div className="flex gap-3">
              <button onClick={() => setRedemptionResult("success")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex flex-col items-center gap-1 ${redemptionResult === "success" ? "bg-green-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="redeem-success"><ThumbsUp className="w-5 h-5" />Got it!</button>
              <button onClick={() => setRedemptionResult("partial")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex flex-col items-center gap-1 ${redemptionResult === "partial" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="redeem-partial"><SkipForward className="w-5 h-5" />Partial</button>
              <button onClick={() => setRedemptionResult("failed")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex flex-col items-center gap-1 ${redemptionResult === "failed" ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="redeem-failed"><ThumbsDown className="w-5 h-5" />Denied</button>
            </div>
            <textarea value={redemptionNotes} onChange={(e) => setRedemptionNotes(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" rows={2} placeholder="Notes (optional)..." data-testid="redeem-notes" />
            <div className="flex gap-3">
              <button onClick={() => setRedemptionModal(null)} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm" data-testid="redeem-cancel">Cancel</button>
              <button onClick={() => handleRedemption(redemptionModal.business_id)} disabled={!redemptionResult} className="flex-1 py-3 rounded-xl bg-sky-500 text-white font-semibold shadow-lg text-sm disabled:opacity-50" data-testid="redeem-submit">Submit</button>
            </div>
          </div>
        </div>
      )}
      <BottomNav />
    </div>
  );
}

function EmptyState({ tab, onAction, actionLabel }) {
  return (
    <div className="flex items-center justify-center min-h-[30vh]" data-testid={`empty-${tab.id}`}>
      <div className="text-center px-8">
        <p className="text-base font-semibold text-slate-700 leading-relaxed">
          {tab.emptyTitle}
        </p>
        <p className="text-sm text-slate-500 mt-1">{tab.emptyDesc}</p>
        <button
          onClick={onAction}
          className="mt-6 px-6 py-3 bg-sky-500 text-white text-sm font-semibold rounded-full"
          data-testid={`empty-${tab.id}-cta`}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

function PlanningCard({ item, navigate, setRedemptionModal, removeTracker, fetchData, daysUntilBirthday }) {
  const cc = getCategoryColor(item.category);
  const isUrgent = !item.in_itinerary || (daysUntilBirthday != null && daysUntilBirthday <= 3);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${cc.light} border ${isUrgent ? "border-amber-300" : cc.border} rounded-xl p-3`}>
      <div className="flex items-center gap-3">
        <BrandLogo name={item.business_name} logoUrl={item.logo_url} size="sm" />
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/offer/${item.business_id}`)}>
          <h3 className="text-sm font-semibold text-slate-800 truncate">{item.business_name}</h3>
          <p className={`text-xs ${cc.text} font-medium`}>{item.category}</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setRedemptionModal(item)} className="px-2.5 py-1.5 rounded-lg bg-green-50 text-green-600 text-xs font-semibold" data-testid={`claim-${item.business_id}`}>Claimed?</button>
          <button onClick={() => removeTracker(item.business_id)} className="p-1.5 rounded-lg bg-red-50 text-red-400" data-testid={`remove-plan-${item.business_id}`}><X className="w-4 h-4" /></button>
        </div>
      </div>
      {/* Urgency badges */}
      {daysUntilBirthday != null && daysUntilBirthday <= 0 && (
        <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full" data-testid="urgency-birthday-only">
          <Gift className="w-3 h-3" />Birthday day — claim today
        </span>
      )}
      {daysUntilBirthday != null && daysUntilBirthday > 0 && daysUntilBirthday <= 3 && (
        <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="urgency-expiring-soon">
          <Clock className="w-3 h-3" />{daysUntilBirthday} day{daysUntilBirthday !== 1 ? "s" : ""} until birthday
        </span>
      )}
      {/* Itinerary status + quick action */}
      {item.in_itinerary ? (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-sky-600 font-medium" data-testid={`in-itin-${item.business_id}`}>
          <ClipboardList className="w-3 h-3" />In your birthday plan
        </div>
      ) : (
        <button
          onClick={async () => {
            try {
              await axios.post(`${API}/itinerary/add-stop`, { business_id: item.business_id });
              toast.success(`Added ${item.business_name} to your plan`);
              fetchData();
            } catch { toast.error("Couldn't add to plan"); }
          }}
          className="mt-2 text-xs text-sky-500 hover:text-sky-700 font-medium"
          data-testid={`add-to-plan-${item.business_id}`}
        >
          + Add to your birthday plan
        </button>
      )}
    </motion.div>
  );
}
